// src/pages/admin/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { getDesigners, getOrders } from "../../lib/db";
import { StatCard, TableCard, Table, Badge, SectionLabel, Spinner, fmt$, fmtDate, Avatar, orderStatusForBadge } from "../../components/UI";

const months = ["Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar","Apr"];
const mockSales = [18200,22400,31000,28600,19800,24000,33100,21400,39200,46800];

export default function AdminDashboard() {
  const [designers, setDesigners] = useState([]);
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([getDesigners(), getOrders()]).then(([d, o]) => {
      setDesigners(d); setOrders(o); setLoading(false);
    });
  }, []);

  if (loading) return <Spinner />;

  const totalSales   = designers.reduce((s, d) => s + (d.ytdSales || 0), 0);
  const totalPending = designers.reduce((s, d) => s + (d.pendingPayout || 0), 0);
  const activeCount  = designers.filter(d => d.status === "active").length;
  const avgOrder     = orders.length ? Math.round(orders.reduce((s,o) => s + (o.amount||0), 0) / orders.length) : 0;
  const maxV         = Math.max(...mockSales);

  const orderCols = [
    { key: "num",        label: "Sales Order #" },
    { key: "designer",   label: "Designer" },
    { key: "client",     label: "Client / Project" },
    { key: "amountFmt",  label: "Amount" },
    { key: "commFmt",    label: "Commission" },
    { key: "statusBadge",label: "Status" },
    { key: "dateFmt",    label: "Date" },
  ];

  const orderRows = orders.slice(0, 8).map(o => ({
    num:         o.orderNumber || o.id.slice(0,8).toUpperCase(),
    designer:    o.designerName || "—",
    client:      o.clientName || "—",
    amountFmt:   fmt$(o.amount),
    commFmt:     fmt$(o.commission),
    statusBadge: <Badge label={orderStatusForBadge(o.status)} />,
    dateFmt:     fmtDate(o.createdAt),
  }));

  return (
    <div>
      <div style={{ padding: "36px 48px 0" }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 300 }}>Program Overview</h1>
        <p style={{ fontSize: 13, color: "var(--mid)", marginTop: 4 }}>
          {new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"})} · {activeCount} active designers · Los Angeles
        </p>
      </div>

      <div style={{ padding: "24px 48px 48px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 28 }}>
          <StatCard label="Total Sales YTD"    value={fmt$(totalSales)}   delta="↑ Live from Firebase" deltaPos />
          <StatCard label="Commissions Pending" value={fmt$(totalPending)} accent delta={`${designers.filter(d=>d.pendingPayout>0).length} pending payouts`} deltaPos />
          <StatCard label="Active Designers"   value={activeCount}        delta="↑ Program members" deltaPos />
          <StatCard label="Avg Order Value"    value={fmt$(avgOrder)}     delta={`${orders.length} total orders`} deltaPos />
        </div>

        <SectionLabel>Monthly Sales (Demo)</SectionLabel>
        <div style={{ background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: 4, padding: "20px 24px 24px", marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 140, marginBottom: 8 }}>
            {mockSales.map((v,i) => {
              const h = Math.round((v/maxV)*130);
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
                  <div title={fmt$(v)} style={{ width: "100%", height: h, background: "var(--gold)", opacity: 0.7, borderRadius: "2px 2px 0 0", transition: "opacity 0.2s", cursor: "default" }}
                    onMouseEnter={e=>e.target.style.opacity=1} onMouseLeave={e=>e.target.style.opacity=0.7} />
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {months.map(m => <div key={m} style={{ flex:1, textAlign:"center", fontSize:9, color:"var(--mid)", letterSpacing:"0.05em" }}>{m}</div>)}
          </div>
        </div>

        <SectionLabel>Recent Orders</SectionLabel>
        <TableCard title="Latest Orders">
          <Table cols={orderCols} rows={orderRows} emptyMsg="No orders yet. Log your first order above." />
        </TableCard>

        <SectionLabel>Top Designers</SectionLabel>
        <TableCard>
          <Table
            cols={[
              { key: "dName", label: "Designer" },
              { key: "tier", label: "Tier" },
              { key: "sales", label: "YTD Sales" },
              { key: "earned", label: "Earned" },
              { key: "pending", label: "Pending" },
            ]}
            rows={[...designers].sort((a,b)=>(b.ytdSales||0)-(a.ytdSales||0)).slice(0,5).map(d=>({
              dName: <div style={{display:"flex",alignItems:"center",gap:10}}><Avatar name={d.name} /><div><div style={{fontWeight:400}}>{d.name}</div><div style={{fontSize:11,color:"var(--mid)"}}>{d.firm}</div></div></div>,
              tier:    <Badge label={d.tier || "Partner"} />,
              sales:   fmt$(d.ytdSales),
              earned:  fmt$(d.ytdEarned),
              pending: fmt$(d.pendingPayout),
            }))}
          />
        </TableCard>
      </div>
    </div>
  );
}
